import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, fromEvent } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { TranslateModule } from '@ngx-translate/core';
import { RxIntuitiveTeethDiagramFacade } from '../../rx-intuitive-teeth-diagram.facade';
import { RxIntuitiveToothEditorComponent } from '../../components/rx-intuitive-tooth-editor/rx-intuitive-tooth-editor.component';
import { RxIntuitiveTeethDiagramComponent } from '../../components/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram.component';
import { RxIntuitiveIndicationButtonsComponent } from '../../components/rx-intuitive-indication-buttons/rx-intuitive-indication-buttons.component';
import { RxIntuitiveTeethTreatmentViewlistComponent } from '../../components/rx-intuitive-teeth-treatment-viewlist/rx-intuitive-teeth-treatment-viewlist.component';
import { RxIntuitiveTeethDiagramFooterComponent } from '@modules/rx-intuitive-teeth-diagram/components/rx-intuitive-teeth-diagram-footer/rx-intuitive-teeth-diagram-footer.component';
import { RxCopyFromToothMenuComponent } from '@modules/rx-intuitive-copy-from-tooth/components/rx-copy-from-tooth-menu/rx-copy-from-tooth-menu.component';
import { RxIntuitiveCopyFromBehaviorService } from '../../services/rx-intuitive-copy-from-behavior.service';
import { RxIntuitiveIndicationRulesService } from '../../services/rx-intuitive-indication-rules.service';
import { RxIntuitiveTeethDiagramLayoutComponent } from '../rx-intuitive-teeth-diagram-layout/rx-intuitive-teeth-diagram-layout.component';
import { IntuitiveIndication } from '../../models';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { TOOTH_MODAL_ACTION } from '@modules/rx-intuitive-teeth-diagram/models/tooth-modal';

@Component({
	selector: 'rx-intuitive-teeth-diagram-container',
	templateUrl: './rx-intuitive-teeth-diagram-container.component.html',
	styleUrls: ['./rx-intuitive-teeth-diagram-container.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		RxIntuitiveTeethDiagramComponent,
		RxIntuitiveIndicationButtonsComponent,
		RxIntuitiveToothEditorComponent,
		RxIntuitiveTeethTreatmentViewlistComponent,
		RxIntuitiveTeethDiagramFooterComponent,
		RxCopyFromToothMenuComponent,
		RxIntuitiveTeethDiagramLayoutComponent
	]
})
export class RxIntuitiveTeethDiagramContainerComponent extends BaseDestroyableComponent implements OnInit {
	@ViewChild(RxIntuitiveTeethDiagramComponent, { read: ElementRef }) private readonly teethDiagram?: ElementRef<HTMLElement>;

	readonly copyFromSourceMenuItems$ = this.copyFromBehaviorService.copyFromSourceMenuItems$;
	readonly isCopyFromToothControlDisabled$ = this.copyFromBehaviorService.isCopyFromMenuEnabled$.pipe(map(enabled => !enabled));
	readonly copyFromMenuDefaultIndicationId: number;

	readonly indications$ = this.facade.indicationToolbarIds$;
	readonly enabledIndications$ = this.facade.enabledIndications$;
	readonly upperJaw$ = this.facade.upperJaw$;
	readonly lowerJaw$ = this.facade.lowerJaw$;
	readonly hasSelectedTeeth$ = this.facade.hasSelectedTeeth$;
	readonly selectedTeethIndicationIds$ = this.facade.selectedTeethIndicationIds$;
	readonly selectedToothIds$ = this.facade.selectedToothIds$;
	readonly editorTeeth$ = this.facade.editorTeeth$;
	readonly previewTreatmentListItems$ = this.facade.previewTreatmentListItems$;
	readonly injectedSpritesContent$ = this.facade.injectedSpritesContent$;

	readonly isToothEditorShown$ = this.facade.isToothEditorShown$;
	readonly canDeleteToothEditor$ = this.facade.canDeleteToothEditor$;
	private readonly pointerUp$ = fromEvent<PointerEvent>(document, 'pointerup');
	readonly canEditToothEditor$ = this.facade.canEditToothEditor$;
	readonly isTeethDiagramReadOnly$ = this.facade.isTeethDiagramReadOnly$;

	/** Renders the treatment list when there are preview teeth, or when only existing bridge summary rows are shown. */
	readonly showTreatmentViewList$ = combineLatest([this.previewTreatmentListItems$, this.isToothEditorShown$]).pipe(
		map(([teethAndBridges, editorShown]) => teethAndBridges.length > 0 && !editorShown)
	);

	/** True when the list, bridge summary rows, or full editor is shown (placeholder hidden). */
	readonly hasSidePanelContent$ = combineLatest([this.showTreatmentViewList$, this.isToothEditorShown$]).pipe(
		map(([showViewlist, showEditor]) => showViewlist || showEditor)
	);

	readonly showCopyFromMenu$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => !readOnly));

	readonly chartReadOnly$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => !!readOnly));

	readonly indicationButtonsDisabled$ = combineLatest([this.hasSelectedTeeth$, this.isTeethDiagramReadOnly$]).pipe(
		map(([hasSelected, readOnly]) => hasSelected !== true || !!readOnly)
	);

	readonly indicationButtonsAsLabel$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => !!readOnly));

	readonly showPanelBack$ = this.hasSidePanelContent$;

	readonly showSidePanelPlaceholder$ = this.hasSidePanelContent$.pipe(map(hasContent => !hasContent));

	constructor(
		protected readonly facade: RxIntuitiveTeethDiagramFacade,
		private readonly copyFromBehaviorService: RxIntuitiveCopyFromBehaviorService,
		private readonly indicationRules: RxIntuitiveIndicationRulesService
	) {
		super();
		this.copyFromMenuDefaultIndicationId = this.indicationRules.getDefaultIndication();
	}

	ngOnInit(): void {
		this.pointerUp$
			.pipe(
				takeUntil(this.componentAlive$),
				withLatestFrom(this.facade.hasSelectedTeeth$),
				filter(([event, hasSelectedTeeth]) => hasSelectedTeeth && !this.isInsideTeethEditingArea(event.target))
			)
			.subscribe(() => this.facade.clearToothSelection());
	}

	onCopyFromToothSourceSelected(toothId: number): void {
		this.facade.copyTreatmentDetailsFromSourceTooth(toothId);
	}

	onToothClicked(toothId: number): void {
		this.facade.toggleToothSelection(toothId);
	}

	onResetSelectedRequested(): void {
		this.facade.resetIndicationForSelectedTeeth();
	}

	onIndicationSelected(indicationId: IntuitiveIndication): void {
		const modalResult$ = this.facade.openModalForUnitTypeIfNeeded(indicationId);

		if (modalResult$) {
			modalResult$.pipe(takeUntil(this.componentAlive$)).subscribe(result => {
				if (result?.action === TOOTH_MODAL_ACTION.Cancel || result?.action === TOOTH_MODAL_ACTION.Delete) {
					return;
				}

				this.facade.assignIndication(indicationId, result?.action === TOOTH_MODAL_ACTION.Save);
			});

			return;
		}

		this.facade.assignIndication(indicationId);
	}

	onOpenTreatmentEditorFromList(toothId: number): void {
		this.facade.openTreatmentEditorFromList(toothId);
	}

	clearSelection(): void {
		this.facade.resetIndicationForSelectedTeeth();
	}

	onToothCrownFormChanged(crownForm: CrownForm): void {
		this.facade.updateToothCrownData(crownForm);
	}

	onToothAdditionalInfoApplied(event: { toothIds: number[]; result: AdditionalInfoResult }): void {
		this.facade.applyToothAdditionalInfo(event.toothIds, event.result);
	}

	onToothClearDisabledFields(event: { toothIds: number[]; payload: Partial<Record<string, unknown>> }): void {
		this.facade.clearDisabledToothFields(event.toothIds, event.payload);
	}

	focusDiagram(): void {
		this.teethDiagram?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
		this.teethDiagram?.nativeElement?.focus({ preventScroll: true });
	}

	private isInsideTeethEditingArea(target: EventTarget | null): boolean {
		const editingAreaComponentSelectors = [
			'.cdk-overlay-container',
			'rx-intuitive-implants-view-modal',
			'rx-copy-from-tooth-menu',
			'rx-intuitive-tooth-editor',
			'rx-intuitive-bridge-editor',
			'rx-intuitive-teeth-diagram',
			'rx-intuitive-teeth-treatment-viewlist',
			'rx-intuitive-indication-button',
			'rx-intuitive-remove-from-bridge-button',
			'[rx-intuitive-bridge-indication-buttons] button',
			'[rx-intuitive-teeth-diagram-footer] button',
			'[rx-intuitive-teeth-diagram-banner]'
		];

		if (target == null) {
			return false;
		}

		let element: Element;

		if (target instanceof Element) {
			element = target;
		} else if (target instanceof Node && target.parentElement != null) {
			element = target.parentElement;
		} else {
			return false;
		}

		return editingAreaComponentSelectors.some(s => element.closest(s) != null);
	}
}
